/**
 * Shared utilities for validation error handling with line numbers
 */

/**
 * Extract line number from an error message
 * Supports patterns like "error on line 14" or "at line 14" or "line 14:"
 */
export function extractLineNumber(message: string): number | null {
  const patterns = [
    /(?:error on |at |)line (\d+)/i,
    /line:?\s*(\d+)/i,
    /:(\d+):\d+/,  // Format like "file:14:27"
  ];
  
  for (const pattern of patterns) {
    const match = message.match(pattern);
    if (match) {
      return parseInt(match[1], 10);
    }
  }
  return null;
}

/**
 * Extract column number from an error message
 */
export function extractColumnNumber(message: string): number | null {
  const patterns = [
    /column (\d+)/i,
    /col(?:umn)?:?\s*(\d+)/i,
    /:\d+:(\d+)/,  // Format like "file:14:27"
  ];
  
  for (const pattern of patterns) {
    const match = message.match(pattern);
    if (match) {
      return parseInt(match[1], 10);
    }
  }
  return null;
}

/**
 * Parse error location from message
 */
export function parseErrorLocation(message: string): { line?: number; column?: number } | null {
  const line = extractLineNumber(message);
  const column = extractColumnNumber(message);
  
  if (line === null && column === null) {
    return null;
  }
  
  return {
    line: line ?? undefined,
    column: column ?? undefined,
  };
}

/**
 * Get line content with optional surrounding context
 */
export function getLineContext(
  content: string,
  lineNumber: number,
  contextLines: number = 1
): {
  targetLine: string;
  lineNumber: number;
  before: { lineNumber: number; content: string }[];
  after: { lineNumber: number; content: string }[];
} | null {
  const lines = content.split('\n');
  
  // Line numbers are 1-indexed
  const index = lineNumber - 1;
  
  if (index < 0 || index >= lines.length) {
    return null;
  }
  
  const before: { lineNumber: number; content: string }[] = [];
  const after: { lineNumber: number; content: string }[] = [];
  
  // Get lines before
  for (let i = Math.max(0, index - contextLines); i < index; i++) {
    before.push({ lineNumber: i + 1, content: lines[i] });
  }
  
  // Get lines after
  for (let i = index + 1; i <= Math.min(lines.length - 1, index + contextLines); i++) {
    after.push({ lineNumber: i + 1, content: lines[i] });
  }
  
  return {
    targetLine: lines[index],
    lineNumber,
    before,
    after,
  };
}

/**
 * Truncate line content for display
 */
export function truncateLine(line: string, maxLength: number = 60): string {
  if (line.length <= maxLength) {
    return line;
  }
  return line.substring(0, maxLength - 3) + '...';
}
